import { holdingsWithTone, rangeLabels, type Holding, type Range } from "./portfolioData";
import type { AccountPlan } from "./billingApi";

export type EstimatePoint = { date: string; index: number };

export type PortfolioBenchmark = {
  symbol: string;
  label: string;
  priceType: string;
  startDate: string;
  endDate: string;
  asOf: string;
  returnPct: number;
  points: EstimatePoint[];
};

export type PortfolioEstimate = {
  methodologyVersion: string;
  range: Range;
  priceType: string;
  startingValue: number;
  endingValue: number;
  startDate: string;
  endDate: string;
  asOf: string;
  returnPct: number;
  points: EstimatePoint[];
  contributions: Array<{ symbol: string; weight: number; returnPct: number; contributionPct: number }>;
  benchmark: PortfolioBenchmark | null;
};

export type PortfolioEstimateRequest = {
  holdings: Array<{ symbol: string; weight: number }>;
  range: Range;
  startingValue: number;
};

export type PublicPortfolioPayload = {
  slug: string;
  name: string;
  description: string;
  holdings: Array<{ symbol: string; name?: string; weight: number }>;
  range: Range;
  startingValue?: number;
  sourceLabel?: string | null;
  sourceUrl?: string | null;
  asOf?: string | null;
  disclosureNote?: string | null;
  viewCount?: number;
};

export type SavedPortfolioPayload = {
  id?: string;
  slug: string;
  name: string;
  description: string;
  visibility?: "private" | "public";
  holdings: Array<{ symbol: string; name?: string; weight: number }>;
  range: string;
  startingValue: number;
  canEdit?: boolean;
  viewCount?: number;
  createdAt?: number;
  updatedAt?: number;
  sourceLabel?: string | null;
  sourceUrl?: string | null;
  asOf?: string | null;
  disclosureNote?: string | null;
};

export type AccountPortfoliosPayload = {
  portfolios: SavedPortfolioPayload[];
  configured?: boolean;
  plan?: AccountPlan;
};

export async function loadPublicPortfolios(): Promise<PublicPortfolioPayload[]> {
  const response = await fetch("/api/portfolios");
  if (!response.ok) return [];
  const payload = await response.json<{ portfolios?: PublicPortfolioPayload[] }>().catch(() => null);
  return Array.isArray(payload?.portfolios) ? payload.portfolios : [];
}

export async function loadSavedPortfolio(slug: string): Promise<SavedPortfolioPayload> {
  const response = await fetch(`/api/portfolios/${encodeURIComponent(slug)}`);
  if (!response.ok) {
    const payload = await response.json<{ error?: string }>().catch(() => null);
    throw new Error(payload?.error ?? "Portfolio not found.");
  }
  const payload = await response.json<SavedPortfolioPayload>().catch(() => null);
  if (!payload) throw new Error("Portfolio not found.");
  return payload;
}

export async function loadAccountPortfolios(): Promise<SavedPortfolioPayload[]> {
  const response = await fetch("/api/account/portfolios");
  if (!response.ok) {
    const payload = await response.json<{ error?: string }>().catch(() => null);
    throw new Error(payload?.error ?? "Your portfolios could not be loaded.");
  }
  const payload = await response.json<AccountPortfoliosPayload>().catch(() => null);
  return Array.isArray(payload?.portfolios) ? payload.portfolios : [];
}

export async function loadPortfolioEstimate(request: PortfolioEstimateRequest, signal?: AbortSignal): Promise<PortfolioEstimate> {
  const response = await fetch("/api/portfolio/estimate", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(request),
    signal,
  });
  if (!response.ok) {
    const payload = await response.json<{ error?: string }>().catch(() => null);
    throw new Error(payload?.error ?? "Historical data is unavailable right now.");
  }
  const payload = await response.json<PortfolioEstimate>().catch(() => null);
  if (!payload) throw new Error("Historical data is unavailable right now.");
  return payload;
}

export function savedPortfolioRoute(payload: SavedPortfolioPayload): {
  name: string;
  description: string;
  tag: string;
  slug: string;
  holdings: Holding[];
  range: Range;
  startingValue: number;
  visibility?: "private" | "public";
  canEdit?: boolean;
  sourceLabel?: string;
  sourceUrl?: string;
  asOf?: string;
  disclosureNote?: string;
} {
  const rawHoldings = Array.isArray(payload.holdings) ? payload.holdings : [];
  return {
    name: payload.name,
    description: payload.description,
    tag: payload.visibility === "public" ? "Published portfolio" : "Private portfolio",
    slug: payload.slug,
    holdings: holdingsWithTone(rawHoldings.map((holding) => ({ symbol: String(holding.symbol ?? "").toUpperCase(), name: String(holding.name ?? holding.symbol ?? ""), weight: Number(holding.weight) }))),
    range: payload.range in rangeLabels ? payload.range as Range : "5y",
    startingValue: Number(payload.startingValue),
    visibility: payload.visibility,
    canEdit: payload.canEdit === true,
    sourceLabel: payload.sourceLabel ?? undefined,
    sourceUrl: payload.sourceUrl ?? undefined,
    asOf: payload.asOf ?? undefined,
    disclosureNote: payload.disclosureNote ?? undefined,
  };
}
